// File Name: pages/terms-and-policy-pages/AppTermsConditions.js
//
// Terms & Conditions for the YNO mobile app. Route: /app/terms
//
// Written against how the app actually behaves, not from a template. The
// clauses that matter most for a sports app are §11 (matches are arranged
// between users, we do not supervise them) and §12 (physical risk) — those are
// the real exposure, not the boilerplate.
//
// Governing law follows the existing website Terms: UAE.

import React from "react";
import { Typography } from "antd";
import { Link } from "react-router-dom";
import LegalPageShell, { APP_LEGAL_CONTACT } from "./LegalPageShell";

const { Title, Paragraph, Text } = Typography;

const AppTermsConditions = () => (
  <LegalPageShell
    title="YNO App — Terms & Conditions"
    subtitle="The agreement between you and Yalla Nellab for use of the YNO mobile application."
  >
    <Title level={4}>1. Agreement</Title>
    <Paragraph>
      1.1 These Terms &amp; Conditions ("Terms") govern your use of the YNO mobile
      application ("App"), operated by Yalla Nellab ("we", "us", "our"), a company
      incorporated in the United Arab Emirates.
    </Paragraph>
    <Paragraph>
      1.2 By creating an account, joining a match, or otherwise using the App, you
      confirm that you have read, understood and agree to be bound by these Terms
      and by our <Link to="/app/privacy">Privacy Policy</Link>. If you do not
      agree, do not use the App.
    </Paragraph>

    <Title level={4}>2. Eligibility</Title>
    <Paragraph>
      2.1 You must be at least 13 years old to hold a YNO account.
    </Paragraph>
    <Paragraph>
      2.2 If you are under 18, you may use the App only with the consent and
      supervision of a parent or guardian, who accepts these Terms on your behalf
      and is responsible for your use of the App.
    </Paragraph>
    <Paragraph>
      2.3 You confirm that the information you give us is accurate and that you
      are not barred from using the App under any applicable law.
    </Paragraph>

    <Title level={4}>3. Your Account</Title>
    <Paragraph>
      3.1 You are responsible for keeping your login details confidential and for
      all activity that happens under your account.
    </Paragraph>
    <Paragraph>
      3.2 <Text strong>Accounts created for you.</Text> A match host may create an
      account using your email address so they can add you to a match. Such an
      account is not fully yours until you claim it. You should claim it through
      the App's "Claim my account" option and set your own password as soon as
      possible. Until you do, treat the account as unsecured.
    </Paragraph>
    <Paragraph>
      3.3 One person may hold one account. Do not impersonate anyone, and do not
      create an account on behalf of someone who has not asked you to.
    </Paragraph>
    <Paragraph>
      3.4 Tell us promptly at <Text strong>{APP_LEGAL_CONTACT}</Text> if you
      believe your account has been accessed without your permission.
    </Paragraph>

    <Title level={4}>4. Guest Participation</Title>
    <Paragraph>
      4.1 A host may add a player to a match as a "guest" without an account. If
      you add someone as a guest, you confirm you have their permission to enter
      their name and, where provided, their phone number or email address.
    </Paragraph>
    <Paragraph>
      4.2 A guest may later claim their record by creating an account with the
      same details.
    </Paragraph>

    <Title level={4}>5. Acceptable Use</Title>
    <Paragraph>You agree not to:</Paragraph>
    <ul>
      <li>Use the App for any unlawful purpose, or to break any applicable law</li>
      <li>
        Harass, threaten, abuse, or discriminate against another user, or post
        content that is offensive, hateful, obscene or defamatory
      </li>
      <li>
        Upload a profile photo, team name, team badge or match name that is
        offensive, misleading, or infringes anyone's rights
      </li>
      <li>Impersonate another person, team or organisation</li>
      <li>
        Deliberately record false match results, statistics or man-of-the-match
        votes
      </li>
      <li>
        Abuse the referral or points system, including by creating accounts that
        do not represent real people
      </li>
      <li>
        Attempt to access accounts, matches or data that are not yours, or to
        probe, scan or test the security of the service
      </li>
      <li>
        Interfere with the App's operation, reverse engineer it, or use automated
        means to access it, except as permitted by law
      </li>
      <li>Collect other users' personal data from the App</li>
    </ul>

    <Title level={4}>6. Content You Provide</Title>
    <Paragraph>
      6.1 You keep ownership of the content you upload, such as your profile photo
      and team badge.
    </Paragraph>
    <Paragraph>
      6.2 You grant us a non-exclusive, worldwide, royalty-free licence to host,
      store, reproduce and display that content for the purpose of operating and
      promoting the App. This licence ends when you delete the content or your
      account, except where it forms part of a match record retained under our
      Privacy Policy.
    </Paragraph>
    <Paragraph>
      6.3 You confirm you have the rights to any content you upload, and that it
      does not infringe anyone else's rights.
    </Paragraph>
    <Paragraph>
      6.4 We may remove content that breaches these Terms, without notice.
    </Paragraph>

    <Title level={4}>7. Matches, Results and Statistics</Title>
    <Paragraph>
      7.1 The person who creates a match is its host. The host records goals and
      the result, and decides the outcome of a drawn match.
    </Paragraph>
    <Paragraph>
      7.2 Statistics, points and leaderboard positions are generated from what
      hosts record. They are provided for enjoyment, are not guaranteed to be
      accurate, and must not be relied on for any competitive, contractual or
      financial purpose.
    </Paragraph>
    <Paragraph>
      7.3 Disputes about a result are between the participants of that match. We
      are not an adjudicator and are under no obligation to intervene, but we may
      correct or remove records that are plainly fabricated or abusive.
    </Paragraph>

    <Title level={4}>8. Teams</Title>
    <Paragraph>
      8.1 A team's owner and captain can manage its roster, invitations and join
      code, and can accept or decline requests to join.
    </Paragraph>
    <Paragraph>
      8.2 If you share a team or match join code, anyone holding it can use it.
      Treat codes as you would an invitation.
    </Paragraph>

    <Title level={4}>9. Points and Referrals</Title>
    <Paragraph>
      9.1 Points and referral rewards have <Text strong>no monetary value</Text>,
      cannot be exchanged for cash, and may be adjusted, expired or withdrawn.
    </Paragraph>
    <Paragraph>
      9.2 A referral reward applies only to a genuinely new account. We may
      cancel points and suspend accounts where we believe the system is being
      abused.
    </Paragraph>

    <Title level={4}>10. Availability and Changes</Title>
    <Paragraph>
      10.1 We provide the App on an "as is" and "as available" basis. We may
      change, suspend or discontinue any part of it, and we may release updates
      you need to install to keep using it.
    </Paragraph>
    <Paragraph>
      10.2 We do not guarantee that the App will be uninterrupted, error-free, or
      free of data loss. Access may depend on your device, your operating system,
      and your internet connection, which are your responsibility.
    </Paragraph>

    <Title level={4}>11. Matches Are Arranged Between Users</Title>
    <Paragraph>
      11.1 <Text strong>
        The App is an organising tool. Matches are arranged directly between
        users.
      </Text>{" "}
      Unless we say so expressly in writing, we do not organise, host, supervise,
      referee, staff or inspect any match, pitch or venue arranged through the
      App, and we are not a party to any arrangement between users.
    </Paragraph>
    <Paragraph>
      11.2 We do not verify the identity, age, fitness, conduct or eligibility of
      any user. You are responsible for deciding who you play with and where.
    </Paragraph>
    <Paragraph>
      11.3 Any payment between users — for pitch hire or anything else — is
      entirely between them. We do not process it and take no responsibility for
      it.
    </Paragraph>

    <Title level={4}>12. Physical Activity and Risk</Title>
    <Paragraph>
      12.1 <Text strong>
        Football and other physical sport carry an inherent risk of injury.
      </Text>{" "}
      By using the App to arrange or join a match, you accept that risk for
      yourself.
    </Paragraph>
    <Paragraph>
      12.2 You confirm that you are medically fit to take part in the activity you
      are joining, and that you will seek professional medical advice if you are
      unsure.
    </Paragraph>
    <Paragraph>
      12.3 To the fullest extent permitted by law, we are not liable for injury,
      illness, death, or loss of or damage to property arising from participation
      in any match or activity arranged through the App.
    </Paragraph>

    <Title level={4}>13. Third-Party Services</Title>
    <Paragraph>
      The App relies on third-party services for sign-in, hosting, image storage,
      email and notifications, and it may link to third-party websites. Their own
      terms and privacy policies govern their services, and we are not responsible
      for them.
    </Paragraph>

    <Title level={4}>14. Intellectual Property</Title>
    <Paragraph>
      The App, its name, logo, design and software are owned by us or our
      licensors and are protected by intellectual property law. We grant you a
      personal, limited, non-exclusive, non-transferable and revocable licence to
      use the App for its intended purpose. You may not copy, modify, distribute,
      sell or lease any part of it.
    </Paragraph>

    <Title level={4}>15. Suspension and Termination</Title>
    <Paragraph>
      15.1 You may stop using the App at any time. You can deactivate your account
      in Settings, or request permanent deletion as described in our{" "}
      <Link to="/app/privacy">Privacy Policy</Link>.
    </Paragraph>
    <Paragraph>
      15.2 We may suspend or terminate your access, with or without notice, if you
      breach these Terms, or where we reasonably believe it is necessary to
      protect other users or the service.
    </Paragraph>
    <Paragraph>
      15.3 Termination does not affect match records already held by other
      participants.
    </Paragraph>

    <Title level={4}>16. Disclaimers</Title>
    <Paragraph>
      To the fullest extent permitted by law, we exclude all warranties,
      conditions and representations that are not expressly set out in these
      Terms, whether express or implied, including any implied warranty of
      merchantability, fitness for a particular purpose, or non-infringement.
    </Paragraph>

    <Title level={4}>17. Limitation of Liability</Title>
    <Paragraph>
      17.1 To the fullest extent permitted by law, we are not liable for any
      indirect, incidental, special or consequential loss, or for loss of profit,
      goodwill, data, or opportunity, arising from your use of the App.
    </Paragraph>
    <Paragraph>
      17.2 Where liability cannot lawfully be excluded, our total liability to you
      is limited to the greater of the amount you have paid us for the App in the
      twelve months before the claim, or AED 500.
    </Paragraph>
    <Paragraph>
      17.3 Nothing in these Terms excludes liability for death or personal injury
      caused by our negligence, for fraud, or for anything else that cannot
      lawfully be excluded.
    </Paragraph>

    <Title level={4}>18. Indemnity</Title>
    <Paragraph>
      You agree to indemnify us against claims, losses and reasonable costs
      arising from your breach of these Terms, your misuse of the App, or content
      you upload.
    </Paragraph>

    <Title level={4}>19. Changes to These Terms</Title>
    <Paragraph>
      We may update these Terms from time to time. We will change the Effective
      Date above, and where a change is significant we will notify you in the App.
      Continuing to use the App after a change means you accept the updated Terms.
    </Paragraph>

    <Title level={4}>20. Governing Law and Jurisdiction</Title>
    <Paragraph>
      20.1 These Terms are governed by and construed in accordance with the laws
      of the United Arab Emirates.
    </Paragraph>
    <Paragraph>
      20.2 Any dispute arising out of or relating to these Terms is subject to the
      exclusive jurisdiction of the courts of the United Arab Emirates.
    </Paragraph>

    <Title level={4}>21. General</Title>
    <Paragraph>
      21.1 If any provision of these Terms is found unenforceable, the rest remain
      in force.
    </Paragraph>
    <Paragraph>
      21.2 Our failure to enforce a right is not a waiver of it.
    </Paragraph>
    <Paragraph>
      21.3 These Terms, together with the Privacy Policy, are the entire agreement
      between you and us regarding the App.
    </Paragraph>

    <Title level={4}>22. Contact</Title>
    <Paragraph>
      Questions about these Terms can be sent to{" "}
      <Text strong>{APP_LEGAL_CONTACT}</Text>.
    </Paragraph>
  </LegalPageShell>
);

export default AppTermsConditions;
